import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '../primitives/Icon';
import { Avatar } from '../primitives/Avatar';
import { useAuth } from '../../../contexts/AuthContext';

export const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!user) return null;

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate('/login');
  };

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-1.5 h-10 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors"
        aria-label="תפריט משתמש"
        aria-expanded={open}
      >
        <Avatar name={user.name} src={user.profileImage} size="sm" />
        <span className="hidden md:inline text-sm font-semibold text-slate-700 dark:text-slate-200 truncate max-w-[120px]">
          {user.name.split(' ')[0]}
        </span>
        <Icon
          name="expand_more"
          size={16}
          className={[
            'text-slate-400 transition-transform',
            open ? 'rotate-180' : '',
          ].join(' ')}
        />
      </button>

      {open && (
        <div
          className="absolute end-0 top-12 w-64 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden z-50"
          role="menu"
        >
          {/* Identity card */}
          <div className="px-4 py-3.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60">
            <div className="flex items-center gap-3">
              <Avatar name={user.name} src={user.profileImage} size="md" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-slate-900 dark:text-white truncate">
                  {user.name}
                </div>
                <div className="text-xs text-slate-500 truncate" dir="ltr">{user.email}</div>
                {user.role === 'super_admin' && (
                  <div className="inline-flex items-center gap-1 text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded mt-1">
                    <Icon name="shield" size={10} /> מנהל ראשי
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="py-1">
            <MenuItem icon="person" label="הפרופיל שלי" onClick={() => go('/profile')} />
            {user.role === 'super_admin' && (
              <MenuItem icon="settings" label="הגדרות מערכת" onClick={() => go('/settings')} />
            )}
          </div>

          <div className="border-t border-slate-100 dark:border-slate-800 py-1">
            <MenuItem
              icon="logout"
              label="התנתקות"
              onClick={handleLogout}
              variant="danger"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const MenuItem: React.FC<{
  icon: string;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
}> = ({ icon, label, onClick, variant = 'default' }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      'w-full flex items-center gap-3 px-4 py-2 text-sm font-semibold transition-colors',
      variant === 'danger'
        ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30'
        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800',
    ].join(' ')}
    role="menuitem"
  >
    <Icon name={icon} size={16} />
    <span className="flex-1 text-start">{label}</span>
  </button>
);
